/**
 * delivery-assembly-cart.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Per-bed independent delivery tier: Standard (free) / Room of Choice (£49.99)
 * / Professional Assembly (£89.99). Mutually exclusive per bed - Assembly
 * already includes room-of-choice delivery, so a bed can only carry ONE of
 * the two paid service lines at a time, never both.
 *
 * Each tier's paid line is linked back to its specific bed via a line item
 * property (_room_bed_key / _assembly_bed_key = that bed's own cart line
 * key), NOT a single cart-wide choice - checking Assembly on one bed never
 * touches any other bed.
 *
 * Association is by cart line KEY (Shopify derives a line's key from its
 * variant + properties, so it only rotates if the bed's own configuration
 * changes - qty changes and cart reordering never rotate it). If a bed's key
 * does change, its old service line becomes orphaned and is cleaned up
 * automatically on the next reconcile.
 *
 * Also drives the product-page pre-selector (product-delivery-assembly.liquid):
 * that snippet stages a tier choice and, once its bed is detected as added to
 * cart, calls window.EvbDeliveryAssembly.applyTierForBedKey() directly to
 * apply it - same code path as clicking a tier button in the cart.
 *
 * Single source of truth = reconcileAll(), run after every cart mutation.
 * ─────────────────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  var SVC_HANDLE = { room: 'room-of-choice-service', assembly: 'assembly-service' };
  var PROP_KEY   = { room: '_room_bed_key', assembly: '_assembly_bed_key' };

  /* ── Classifier config (mirrors the bed-detection logic in cart-items.liquid) ── */
  var IGNORE_RE    = /swatch|sample|protector|mattress topper|\btopper\b|pillow|recycl|\bbolt|\bscrew|wall panel|foot ?stool|duvet|bedding/i;
  var HEADBOARD_RE = /headboard/i;
  var MATTRESS_RE  = /mattress/i;
  var BED_RE       = /\b(bed|divan|ottoman|sleigh)\b/i;
  var BED_TYPES    = ['beds & bed frames', 'divan beds', 'sleigh bed'];

  var serviceVariantId = {}; // { room: id, assembly: id }
  var serviceHandleById = {}; // reverse lookup: variantId -> 'room' | 'assembly'
  var isBusy = false;

  /* Single-flight lock that RETRIES ITSELF rather than dropping the call -
     two different call sites (reconcileAll's mutation watcher, and a tier
     button / the product-page pre-selector calling applyTierForBedKey)
     can race for the same lock. A generic "something else wants a turn"
     flag loses the SPECIFIC request (which bed/tier) once the lock frees up;
     retrying the exact same call is the only way to guarantee it still runs. */
  function withLock(fn) {
    if (isBusy) {
      return new Promise(function (resolve) {
        setTimeout(function () { resolve(withLock(fn)); }, 200);
      });
    }
    isBusy = true;
    return fn().catch(function (e) { console.warn('[DAC]', e); }).then(function (result) {
      isBusy = false;
      return result;
    });
  }

  function j(url, opts) {
    return fetch(url, opts).then(function (r) {
      if (!r.ok) throw new Error('[DAC] ' + url + ' -> ' + r.status);
      return r.json();
    });
  }

  function classify(item) {
    if (item.handle === SVC_HANDLE.room || item.handle === SVC_HANDLE.assembly) return 'service';
    var title = (item.product_title || item.title || '');
    var type  = (item.product_type || '').toLowerCase();
    var hay   = title + ' ' + type;
    var isBed = BED_RE.test(hay) || BED_TYPES.indexOf(type) !== -1;
    if (IGNORE_RE.test(hay)) return 'ignore';
    // 'headboard' alone doesn't disqualify a bed - many real bed models are named
    // e.g. "... Floorstanding Headboard". Only ignore it as a standalone headboard
    // product (no bed/divan/ottoman/sleigh word alongside it).
    if (HEADBOARD_RE.test(hay) && !isBed) return 'ignore';
    if (MATTRESS_RE.test(hay) && !isBed) return 'mattress';
    if (isBed) return 'bed';
    return 'ignore';
  }

  function loadServiceVariants() {
    return Promise.all([
      j('/products/' + SVC_HANDLE.room + '.js').catch(function () { return null; }),
      j('/products/' + SVC_HANDLE.assembly + '.js').catch(function () { return null; })
    ]).then(function (res) {
      if (res[0]) { serviceVariantId.room = res[0].variants[0].id; serviceHandleById[res[0].variants[0].id] = 'room'; }
      if (res[1]) { serviceVariantId.assembly = res[1].variants[0].id; serviceHandleById[res[1].variants[0].id] = 'assembly'; }
    }).catch(function (e) { console.warn('[DAC] could not load service products', e); });
  }

  function bedLines(cart) {
    return cart.items.filter(function (it) { return classify(it) === 'bed'; });
  }

  function serviceLineForBedKey(cart, tier, bedKey) {
    var variantId = serviceVariantId[tier];
    var propKey = PROP_KEY[tier];
    return cart.items.find(function (it) {
      return classify(it) === 'service' && it.variant_id === variantId &&
        it.properties && it.properties[propKey] === bedKey;
    });
  }

  function currentTierForBed(cart, bedKey) {
    if (serviceLineForBedKey(cart, 'assembly', bedKey)) return 'assembly';
    if (serviceLineForBedKey(cart, 'room', bedKey)) return 'room';
    return 'none';
  }

  /* ── DOM sync: every tier-group for a given bed key, drawer + cart page alike ── */
  function syncTierButtons(cart) {
    var beds = bedLines(cart);
    var seenKeys = {};

    beds.forEach(function (bed) {
      seenKeys[bed.key] = true;
      var tier = currentTierForBed(cart, bed.key);
      document.querySelectorAll('[data-dac-tier-group][data-bed-line-key="' + CSS.escape(bed.key) + '"]').forEach(function (group) {
        group.querySelectorAll('[data-dac-tier-btn]').forEach(function (btn) {
          var isSel = btn.dataset.tier === tier;
          btn.classList.toggle('is-selected', isSel);
          btn.setAttribute('aria-checked', String(isSel));
          btn.disabled = false;
        });
      });
    });

    document.querySelectorAll('[data-dac-tier-group]').forEach(function (group) {
      var key = group.dataset.bedLineKey;
      if (key && !seenKeys[key]) {
        group.querySelectorAll('[data-dac-tier-btn]').forEach(function (btn) { btn.disabled = true; });
      }
    });
  }

  /* ── Apply one bed's wanted tier (mutually exclusive) ───────────────────── */
  function applyTierForBedKey(bedKey, wantedTier) {
    return withLock(function () { return applyTierForBedKeyLocked(bedKey, wantedTier); });
  }

  function applyTierForBedKeyLocked(bedKey, wantedTier) {
    return j('/cart.js').then(function (cart) {
      var bed = bedLines(cart).find(function (b) { return b.key === bedKey; });
      if (!bed) return null;

      var chain = Promise.resolve();
      ['room', 'assembly'].forEach(function (tier) {
        var existing = serviceLineForBedKey(cart, tier, bedKey);
        if (tier === wantedTier) {
          if (existing && existing.quantity !== bed.quantity) {
            chain = chain.then(function () {
              return j('/cart/change.js', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: existing.key, quantity: bed.quantity })
              });
            });
          } else if (!existing && serviceVariantId[tier]) {
            chain = chain.then(function () {
              var props = {};
              props[PROP_KEY[tier]] = bedKey;
              // Human-readable, non-underscore property (Shopify hides "_"-prefixed
              // properties everywhere, including the packing slip) so the printed
              // slip shows exactly which bed this line belongs to.
              props['For Bed'] = bed.title || bed.product_title || '';
              return j('/cart/add.js', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: serviceVariantId[tier], quantity: bed.quantity, properties: props })
              });
            });
          }
        } else if (existing) {
          chain = chain.then(function () {
            return j('/cart/change.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: existing.key, quantity: 0 })
            });
          });
        }
      });

      return chain.then(function () {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        return j('/cart.js');
      }).then(function (freshCart) {
        syncTierButtons(freshCart);
      });
    });
  }

  /* ── Reconcile: fix up quantities + drop orphans, sync all UI ───────────── */
  function reconcileAll() {
    return withLock(reconcileAllLocked);
  }

  function reconcileAllLocked() {
    return j('/cart.js').then(function (cart) {
      syncTierButtons(cart);
      if (!serviceVariantId.room && !serviceVariantId.assembly) return null;

      var beds = bedLines(cart);
      var bedByKey = {};
      beds.forEach(function (b) { bedByKey[b.key] = b; });

      var toRemove = [];      // line keys with no bed to belong to
      var toRequantify = [];  // {lineKey, quantity} - bed qty changed since the service line was added

      cart.items.forEach(function (it) {
        if (classify(it) !== 'service') return;
        var tier = serviceHandleById[it.variant_id];
        if (!tier) return;
        var bedKey = it.properties && it.properties[PROP_KEY[tier]];
        var bed = bedKey && bedByKey[bedKey];
        if (!bed) { toRemove.push(it.key); return; }
        if (it.quantity !== bed.quantity) toRequantify.push({ lineKey: it.key, quantity: bed.quantity });
      });

      if (!toRemove.length && !toRequantify.length) return null;

      var chain = Promise.resolve();
      toRemove.forEach(function (lineKey) {
        chain = chain.then(function () {
          return j('/cart/change.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: lineKey, quantity: 0 })
          });
        });
      });
      toRequantify.forEach(function (r) {
        chain = chain.then(function () {
          return j('/cart/change.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: r.lineKey, quantity: r.quantity })
          });
        });
      });

      return chain.then(function () { return j('/cart.js'); }).then(function (freshCart) {
        syncTierButtons(freshCart);
      });
    });
  }

  /* ── Events ─────────────────────────────────────────────────────────────── */
  function bind() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-dac-tier-btn]');
      if (!btn || btn.disabled) return;
      var group = btn.closest('[data-dac-tier-group]');
      if (!group) return;
      var bedKey = btn.dataset.bedLineKey;
      var tier = btn.dataset.tier;
      var wasSelected = btn.classList.contains('is-selected');
      var wantedTier = wasSelected ? 'none' : tier;

      group.querySelectorAll('[data-dac-tier-btn]').forEach(function (b) {
        b.classList.remove('is-selected');
        b.setAttribute('aria-checked', 'false');
        b.disabled = true;
      });
      if (wantedTier !== 'none') {
        btn.classList.add('is-selected');
        btn.setAttribute('aria-checked', 'true');
      }

      applyTierForBedKey(bedKey, wantedTier);
    });

    var t;
    function onCartChange() {
      clearTimeout(t);
      t = setTimeout(function () { reconcileAll(); }, 300);
    }
    ['cart:updated', 'cart:refresh', 'theme:cart:updated', 'theme:cart:refresh', 'theme:cart:added', 'cart-drawer:open', 'shopify:section:load']
      .forEach(function (ev) { document.addEventListener(ev, onCartChange); });

    // Safety net: catch ANY cart mutation (theme, apps, custom buttons) -
    // guarded against re-triggering on our OWN in-flight requests via isBusy.
    if (!window.__dacFetchPatched) {
      window.__dacFetchPatched = true;
      var of = window.fetch;
      window.fetch = function () {
        var url = (arguments[0] && arguments[0].url) || arguments[0] || '';
        var isMut = typeof url === 'string' && /\/cart\/(add|change|update|clear)/.test(url);
        var p = of.apply(this, arguments);
        if (isMut && !isBusy) p.then(function () { document.dispatchEvent(new CustomEvent('dac:mutation')); }).catch(function () {});
        return p;
      };
    }
    document.addEventListener('dac:mutation', onCartChange);
  }

  function init() {
    loadServiceVariants().then(function () { bind(); reconcileAll(); });
  }
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();

  // Exposed for the product-page pre-selector (product-delivery-assembly.liquid)
  // to apply a staged tier once it detects its bed has been added to cart.
  window.EvbDeliveryAssembly = {
    applyTierForBedKey: function (bedKey, tier) {
      return loadServiceVariants().then(function () { return applyTierForBedKey(bedKey, tier); });
    }
  };
})();
